import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Divider, FlexBox, Text } from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { ApiErrorStatusCode } from 'api/api';
import { DecryptPayloadErrorResponse, messengersApi } from 'apis/messengers';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { AutomaticUserVerificationSetup, JwtValidation, MessengerSecretModal, } from './components';
/** Details for verified authentication. */
export function VerifiedSettings({ messengerId }) {
    const [decryptToken] = messengersApi.useDecryptTokenMutation();
    const { data: secrets = [], isFetching } = messengersApi.useReadSecretsQuery({
        messengerId,
    });
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const [jwtValidationState, setJwtValidationState] = useState({
        type: 'initialized',
    });
    const handleOnAddSecret = () => {
        dispatch(appActions.showModal({
            modal: {
                type: 'settings:messengerSecret',
                arg: {
                    action: { type: 'add' },
                    messengerId,
                },
            },
        }));
    };
    const handleOnEditSecret = (secretId) => {
        const secret = secrets.find((x) => x.id === secretId);
        if (secret) {
            dispatch(appActions.showModal({
                modal: {
                    type: 'settings:messengerSecret',
                    arg: {
                        action: { type: 'update', secret },
                        messengerId,
                    },
                },
            }));
        }
    };
    const handleOnDeleteSecret = (secretId) => {
        dispatch(appActions.showModal({
            modal: {
                type: 'settings:messengerSecret',
                arg: {
                    action: { type: 'delete', secretId },
                    messengerId,
                },
            },
        }));
    };
    const handleOnDecryptToken = (payload) => {
        setJwtValidationState({ type: 'decoding' });
        decryptToken({ messengerId, payload })
            .unwrap()
            .then(({ decrypted }) => {
            setJwtValidationState({
                type: 'decoding-success',
                decrypted,
            });
        })
            .catch((error) => {
            if (error.status === ApiErrorStatusCode.BadRequest) {
                const { data } = error;
                setJwtValidationState({
                    type: 'decoding-failure',
                    error: DecryptPayloadErrorResponse.is(data)
                        ? DecryptPayloadErrorResponse.mkErrorMessage(data)
                        : { message: 'Unknown error.' },
                });
            }
            else {
                setJwtValidationState({ type: 'initialized' });
                notify({ title: 'An error occurred while decoding jwt token' }, NotificationType.Error);
            }
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, Object.assign({ margin: "400 0" }, { children: _jsx(Divider, { size: "xSmall" }) })), _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "300" }, { children: [_jsx(Text.Heading, Object.assign({ variant: "heading-3" }, { children: "Programmatically user authentication" })), _jsxs(Text.Paragraph, { children: ["This configuration is required to use the\u00A0", _jsx("strong", { children: "Programmatically authenticate the users" }), " option. The system uses a JSON Web Token (JWT) protected with a hash-based message authentication code (HMAC) to verify the user's identity. You need to add and configure your private keys."] }), _jsx(AutomaticUserVerificationSetup, { secrets: secrets, isLoading: isFetching, onAdd: handleOnAddSecret, onEdit: handleOnEditSecret, onDelete: handleOnDeleteSecret })] })), _jsx(Box, Object.assign({ margin: "400 0 0" }, { children: _jsx(JwtValidation, { state: jwtValidationState, onDecrypt: handleOnDecryptToken }) })), _jsx(MessengerSecretModal, {})] }));
}
